import { useCallback, useState } from 'react';
import {
  StyleSheet,
  Text,
  View,
  type NativeSyntheticEvent,
  type TextLayoutEventData,
} from 'react-native';

import { TextLink } from '@/components/atoms/link';
import { colorSemantic } from '@/theme';

const DEFAULT_COLLAPSED_LINES = 5;

export interface ExpandableTextProps {
  readonly text: string;
  readonly expandLabel: string;
  readonly collapseLabel?: string;
  readonly collapsedNumberOfLines?: number;
  readonly testID?: string;
}

export const ExpandableText = ({
  text,
  expandLabel,
  collapseLabel,
  collapsedNumberOfLines = DEFAULT_COLLAPSED_LINES,
  testID,
}: ExpandableTextProps) => {
  const [totalLines, setTotalLines] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);

  const handleTextLayout = useCallback((event: NativeSyntheticEvent<TextLayoutEventData>) => {
    setTotalLines(event.nativeEvent.lines.length);
  }, []);

  const toggle = useCallback(() => setExpanded((current) => !current), []);

  const id = testID ?? 'expandable-text';
  const overflows = totalLines !== null && totalLines > collapsedNumberOfLines;
  const canCollapse = collapseLabel !== undefined;
  const showToggle = overflows && (!expanded || canCollapse);

  return (
    <View style={styles.container} testID={testID}>
      <Text
        numberOfLines={expanded ? undefined : collapsedNumberOfLines}
        style={styles.body}
        testID={`${id}-body`}>
        {text}
      </Text>
      <Text
        accessibilityElementsHidden={true}
        importantForAccessibility="no-hide-descendants"
        onTextLayout={handleTextLayout}
        style={[styles.body, styles.measure]}
        testID={`${id}-measure`}>
        {text}
      </Text>
      {showToggle ? (
        <TextLink
          flush={true}
          label={expanded && canCollapse ? collapseLabel : expandLabel}
          onPress={toggle}
          testID={`${id}-toggle`}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
    width: '100%',
  },
  body: {
    color: colorSemantic.colorTextNdPrimary,
    fontSize: 16,
    letterSpacing: 0.5,
    lineHeight: 24,
    width: '100%',
  },
  measure: {
    left: 0,
    opacity: 0,
    position: 'absolute',
    right: 0,
    top: 0,
    zIndex: -1,
  },
});
